import { useMemo, useState } from 'react';

import type { TableSourceDescriptor, VectorSourceDescriptor } from '../../../data/model';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { calculateJoinDiagnostics, canApplyJoin } from '../../../joins/diagnostics';
import { isExactJoinRelationship, type JoinConfig } from '../../../joins/model';
import { joinKeyFields, suggestJoinKeys } from '../../../joins/suggestions';
import type { LayerDefinition } from '../../../project/model';
import { useAppStore } from '../../state/useAppStore';

interface JoinsEditorProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
  readonly tables: readonly TableSourceDescriptor[];
}

function defaultSelectedFields(
  table: TableSourceDescriptor,
  rightField: string,
): readonly string[] {
  const eligible = table.fields.filter((field) => field.name !== rightField);
  return eligible.slice(0, eligible.length <= 12 ? eligible.length : 10).map((field) => field.name);
}

function countLabel(value: number, singular: string, plural = `${singular}s`): string {
  return `${value.toLocaleString()} ${value === 1 ? singular : plural}`;
}

export function JoinsEditor({ layer, source, tables }: JoinsEditorProps) {
  const [editor, setEditor] = useState<
    { readonly kind: 'create' } | { readonly kind: 'edit'; readonly joinId: string } | null
  >(null);
  const [tableId, setTableId] = useState(tables[0]?.id ?? '');
  const table = tables.find((candidate) => candidate.id === tableId) ?? tables[0];
  const [initialKeys] = useState(() =>
    table ? suggestJoinKeys(source, table) : { left: '', right: '' },
  );
  const [leftField, setLeftField] = useState(initialKeys.left);
  const [rightField, setRightField] = useState(initialKeys.right);
  const [selectedFields, setSelectedFields] = useState<readonly string[]>(
    table ? defaultSelectedFields(table, initialKeys.right) : [],
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{
    readonly joinId: string;
    readonly joinName: string;
    readonly returnFocusTo: HTMLButtonElement;
  } | null>(null);
  const addJoin = useAppStore((state) => state.addLayerJoin);
  const updateJoin = useAppStore((state) => state.updateLayerJoin);
  const inspectJoinRemoval = useAppStore((state) => state.inspectLayerJoinRemoval);
  const removeJoin = useAppStore((state) => state.removeLayerJoin);

  const diagnostics = useMemo(
    () =>
      table && leftField && rightField
        ? calculateJoinDiagnostics(source, table, leftField, rightField)
        : null,
    [leftField, rightField, source, table],
  );
  const eligibleFields = table?.fields.filter((field) => field.name !== rightField) ?? [];
  const editedJoinId = editor?.kind === 'edit' ? editor.joinId : null;
  const duplicateJoin =
    table && leftField && rightField
      ? layer.joins.find(
          (join) =>
            join.id !== editedJoinId &&
            isExactJoinRelationship(join, {
              tableSourceId: table.id,
              leftField,
              rightField,
            }),
        )
      : undefined;

  const openCreate = () => {
    const next = tables[0];
    setEditor({ kind: 'create' });
    setNotice(null);
    if (!next) return;
    const keys = suggestJoinKeys(source, next);
    setTableId(next.id);
    setLeftField(keys.left);
    setRightField(keys.right);
    setSelectedFields(defaultSelectedFields(next, keys.right));
  };

  const openEdit = (join: JoinConfig) => {
    setEditor({ kind: 'edit', joinId: join.id });
    setTableId(join.tableSourceId);
    setLeftField(join.leftField);
    setRightField(join.rightField);
    setSelectedFields(join.fields.map((field) => field.sourceField));
    setNotice(null);
  };

  const selectTable = (nextTableId: string) => {
    const next = tables.find((candidate) => candidate.id === nextTableId);
    setTableId(nextTableId);
    if (!next) return;
    const keys = suggestJoinKeys(source, next);
    setLeftField(keys.left);
    setRightField(keys.right);
    setSelectedFields(defaultSelectedFields(next, keys.right));
    setNotice(null);
  };

  return (
    <section className="inspector-section joins-section">
      <div className="style-section-heading">
        <h3>Joined data</h3>
        <span>{(layer.joins ?? []).length}</span>
      </div>
      {(layer.joins ?? []).map((join) => {
        const joinedTable = tables.find((candidate) => candidate.id === join.tableSourceId);
        const summary = joinedTable
          ? calculateJoinDiagnostics(source, joinedTable, join.leftField, join.rightField)
          : null;
        return (
          <div className="applied-join" key={join.id}>
            <strong>{join.name}</strong>
            <span>
              {join.leftField} → {join.rightField}
            </span>
            <small>
              {summary
                ? `${summary.matchedFeatureCount.toLocaleString()} / ${summary.leftFeatureCount.toLocaleString()} matched · ${join.fields.length.toLocaleString()} ${join.fields.length === 1 ? 'field' : 'fields'}`
                : `${join.fields.length.toLocaleString()} ${join.fields.length === 1 ? 'field' : 'fields'}`}
            </small>
            <div className="applied-join-actions">
              <button onClick={() => openEdit(join)} type="button">
                Edit
              </button>
              <button
                onClick={(event) => {
                  const inspection = inspectJoinRemoval(layer.id, join.id);
                  if (!inspection.ok) {
                    setNotice(inspection.message);
                    return;
                  }
                  setNotice(null);
                  setPendingRemoval({
                    joinId: join.id,
                    joinName: inspection.impact.joinName,
                    returnFocusTo: event.currentTarget,
                  });
                }}
                type="button"
              >
                Remove
              </button>
            </div>
          </div>
        );
      })}

      {!editor ? (
        <button className="secondary-button" onClick={openCreate} type="button">
          Add join
        </button>
      ) : tables.length === 0 ? (
        <div className="join-empty-state">
          <p>Add a table first.</p>
          <button onClick={() => setEditor(null)} type="button">
            Close
          </button>
        </div>
      ) : table ? (
        <div className="join-editor">
          <strong className="join-editor-title">
            {editor.kind === 'edit' ? 'Edit join' : 'Join data'}
          </strong>
          <label>
            Table
            <select onChange={(event) => selectTable(event.target.value)} value={table.id}>
              {tables.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Match layer field
            <select onChange={(event) => setLeftField(event.target.value)} value={leftField}>
              {joinKeyFields(source.fields).map((field) => (
                <option key={field.name} value={field.name}>
                  {field.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            to table field
            <select
              onChange={(event) => {
                const next = event.target.value;
                setRightField(next);
                setSelectedFields((current) => current.filter((field) => field !== next));
                setNotice(null);
              }}
              value={rightField}
            >
              {joinKeyFields(table.fields).map((field) => (
                <option key={field.name} value={field.name}>
                  {field.name}
                </option>
              ))}
            </select>
          </label>

          {diagnostics ? (
            <div className="join-diagnostics" role="status">
              {diagnostics.incompatibility ? <strong>{diagnostics.incompatibility}</strong> : null}
              <span>
                {diagnostics.matchedFeatureCount.toLocaleString()} of{' '}
                {diagnostics.leftFeatureCount.toLocaleString()} features matched
              </span>
              <span>
                {countLabel(diagnostics.unmatchedFeatureCount, 'unmatched feature')} ·{' '}
                {countLabel(diagnostics.unusedTableRowCount, 'unused table row')}
              </span>
              {diagnostics.blankLeftKeyCount || diagnostics.blankRightKeyCount ? (
                <span>
                  {diagnostics.blankLeftKeyCount.toLocaleString()} blank layer keys ·{' '}
                  {diagnostics.blankRightKeyCount.toLocaleString()} blank table keys
                </span>
              ) : null}
              {diagnostics.duplicateRightKeyCount > 0 ? (
                <strong>
                  Cannot join: {countLabel(diagnostics.duplicateRightKeyCount, 'duplicate value')}{' '}
                  in the table key.
                </strong>
              ) : null}
              {duplicateJoin ? (
                <div className="join-duplicate-message">
                  <strong>This join already exists.</strong>
                  <button onClick={() => openEdit(duplicateJoin)} type="button">
                    Edit existing join
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="join-field-heading">
            <strong>Fields to add</strong>
            <span>
              {selectedFields.length} of {eligibleFields.length} selected
            </span>
          </div>
          <div className="popup-field-bulk-actions">
            <button
              onClick={() => setSelectedFields(eligibleFields.map((field) => field.name))}
              type="button"
            >
              Select all
            </button>
            <button onClick={() => setSelectedFields([])} type="button">
              Clear all
            </button>
          </div>
          <div className="join-field-list">
            {eligibleFields.map((field) => (
              <label key={field.name}>
                <input
                  checked={selectedFields.includes(field.name)}
                  onChange={(event) =>
                    setSelectedFields((current) =>
                      event.target.checked
                        ? [...current, field.name]
                        : current.filter((name) => name !== field.name),
                    )
                  }
                  type="checkbox"
                />
                {field.name}
              </label>
            ))}
          </div>
          <div className="join-actions">
            <button
              onClick={() => {
                setEditor(null);
                setNotice(null);
              }}
              type="button"
            >
              Cancel
            </button>
            <button
              className="primary-action"
              disabled={
                !diagnostics ||
                !canApplyJoin(diagnostics, selectedFields.length) ||
                Boolean(duplicateJoin)
              }
              onClick={() => {
                const draft = {
                  tableSourceId: table.id,
                  leftField,
                  rightField,
                  fields: selectedFields,
                };
                const result =
                  editor.kind === 'edit'
                    ? updateJoin(layer.id, editor.joinId, draft)
                    : addJoin(layer.id, draft);
                setNotice(result.ok ? null : result.message);
                if (result.ok) setEditor(null);
              }}
              type="button"
            >
              {editor.kind === 'edit' ? 'Apply changes' : 'Add join'}
            </button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <p className="style-notice" role="status">
          {notice}
        </p>
      ) : null}
      {pendingRemoval ? (
        <ConfirmationDialog
          confirmLabel={'Remove join'}
          confirmTone="danger"
          description={
            <>
              <p>Joined attributes will be removed from {layer.name}.</p>
              <p>The source table “{pendingRemoval.joinName}” will remain in the project.</p>
            </>
          }
          onCancel={() => setPendingRemoval(null)}
          onConfirm={() => {
            const result = removeJoin(layer.id, pendingRemoval.joinId);
            if (result.ok) setPendingRemoval(null);
            setNotice(result.ok ? null : result.message);
          }}
          returnFocusTo={pendingRemoval.returnFocusTo}
          title="Remove join?"
        />
      ) : null}
    </section>
  );
}
